<template>
  <div class="ln-page">
    <div class="ln-wrap">
      <header class="ln-head" data-reveal>
        <p class="ln-eyebrow">Dashboard</p>
        <h1 class="ln-display">Plan</h1>
        <p class="ln-lede">
          Manage your subscription, compare plans, and review past invoices.
        </p>
      </header>

      <section class="ln-plan-card" data-reveal>
        <div class="ln-plan-card__info">
          <p class="ln-plan-card__label">Your plan</p>
          <p class="ln-plan-card__value">{{ userPlan.plan.label }}</p>
          <p
            v-if="userPlan.user.paddle_cancellation_effective_date"
            class="ln-plan-card__meta ln-plan-card__meta--warn"
          >
            Cancels on
            {{ getDayDate(userPlan.user.paddle_cancellation_effective_date) }}
          </p>
          <p
            v-else-if="userPlan.user.padle_next_bill_date"
            class="ln-plan-card__meta"
          >
            Next payment due
            {{ getDayDate(userPlan.user.padle_next_bill_date) }}
          </p>
          <p
            v-if="
              userPlan.plan.name !== 'free' &&
              paddleUser &&
              paddleUser.payment_information
            "
            class="ln-plan-card__meta"
          >
            {{ capitalize(paddleUser.payment_information.payment_method) }}
            <template
              v-if="
                'last_four_digits' in paddleUser.payment_information &&
                typeof paddleUser.payment_information.last_four_digits ===
                  'string' &&
                paddleUser.payment_information.last_four_digits
              "
              >&#8226;&#8226;&#8226;&#8226;
              {{ paddleUser.payment_information.last_four_digits }}</template
            >
            <template
              v-if="
                'card_type' in paddleUser.payment_information &&
                typeof paddleUser.payment_information.card_type === 'string' &&
                paddleUser.payment_information.card_type
              "
              >({{ paddleUser.payment_information.card_type }})</template
            >
          </p>
          <form
            v-if="editingBillingEmail"
            class="ln-billing-email"
            @submit.prevent="saveBillingEmail"
          >
            <v-text-field
              v-model="billingEmailInput"
              class="ln-billing-email__input"
              type="email"
              label="Billing email"
              autocomplete="email"
              variant="outlined"
              density="compact"
              hide-details
              :disabled="savingBillingEmail"
              data-testid="billing-email-input"
            ></v-text-field>
            <div class="ln-billing-email__actions">
              <button
                type="submit"
                class="ln-btn ln-btn--quiet ln-btn--sm"
                :disabled="savingBillingEmail"
              >
                Save
              </button>
              <button
                type="button"
                class="ln-btn ln-btn--quiet ln-btn--sm"
                :disabled="savingBillingEmail"
                @click="editingBillingEmail = false"
              >
                Cancel
              </button>
            </div>
          </form>
          <p
            v-else-if="billingEmail"
            class="ln-plan-card__meta"
            data-testid="billing-email"
          >
            Billing email: {{ billingEmail }}
            <button
              type="button"
              class="ln-inline-link"
              @click="editBillingEmail"
            >
              Edit
            </button>
          </p>
          <p
            v-if="!isAdmin"
            class="ln-plan-card__meta"
            data-testid="billing-managed-by-admins"
          >
            Billing is managed by your team's admins.
          </p>
        </div>
        <div class="ln-plan-card__actions">
          <button-of-plan
            v-if="isAdmin && userPlan.user.paddle_cancellation_effective_date"
            :planName="userPlan.plan.name"
            smallBtn
          />
          <button
            v-if="isAdmin"
            type="button"
            class="ln-btn ln-btn--quiet"
            :disabled="openingPaddleUpdateUrl"
            @click="openPaddleUpdateUrl"
          >
            <v-icon
              v-if="openingPaddleUpdateUrl"
              size="small"
              class="ln-btn__icon ln-spin"
              >mdi-loading</v-icon
            >
            <span
              v-if="
                userPlan.plan.name !== 'free' &&
                !userPlan.user.paddle_cancellation_effective_date
              "
              >Subscription management</span
            >
            <span v-else>Payment history</span>
          </button>
        </div>
      </section>

      <section class="ln-bento" data-reveal>
        <article class="ln-tile">
          <p class="ln-tile__label">Links created</p>
          <p class="ln-tile__value">{{ formatCount(totalLinksCounter) }}</p>
          <div class="ln-meter" v-if="linksUsage !== null">
            <span
              v-if="linksUsage > 0"
              class="ln-meter__fill"
              :style="{ width: linksUsage + '%' }"
            ></span>
          </div>
          <p class="ln-tile__meta">
            Plan limit
            {{
              userPlanObject.max_links === -1
                ? "Unlimited"
                : formatCount(userPlanObject.max_links)
            }}
          </p>
        </article>

        <article class="ln-tile">
          <p class="ln-tile__label">Clicks this month</p>
          <p class="ln-tile__value">{{ formatCount(monthlyClicksCounter) }}</p>
          <div class="ln-meter" v-if="clicksUsage !== null">
            <span
              v-if="clicksUsage > 0"
              class="ln-meter__fill"
              :style="{ width: clicksUsage + '%' }"
            ></span>
          </div>
          <p class="ln-tile__meta">
            Plan limit
            {{
              userPlanObject.monthly_clicks === -1
                ? "Unlimited"
                : formatCount(userPlanObject.monthly_clicks)
            }}
          </p>
        </article>
      </section>

      <section class="ln-panel" data-reveal>
        <div class="ln-section__head">
          <v-icon class="ln-section__icon">mdi-view-grid-outline</v-icon>
          <h2 class="ln-section__title">Available plans</h2>
          <a
            class="ln-inline-link ln-section__head-link"
            href="https://1link.io/pricing#features"
            target="_blank"
            rel="noopener"
            >Full feature list</a
          >
        </div>

        <div class="ln-toggle-row">
          <div class="ln-toggle" role="group" aria-label="Billing cycle">
            <button
              type="button"
              class="ln-toggle__opt"
              :class="{ 'is-active': billingCycle === 'monthly' }"
              @click="billingCycle = 'monthly'"
            >
              Monthly
            </button>
            <button
              type="button"
              class="ln-toggle__opt"
              :class="{ 'is-active': billingCycle === 'yearly' }"
              @click="billingCycle = 'yearly'"
            >
              Yearly <span class="ln-toggle__badge">Save 10%</span>
            </button>
          </div>
          <p
            v-if="!priceLoading && pricePreviewCurrencyCode"
            class="ln-toggle-row__note"
          >
            All prices include VAT, charged in
            <strong>{{ pricePreviewCurrencyCode }}</strong>
          </p>
        </div>

        <div class="ln-pricing-scroll">
          <table
            class="ln-pricing"
            :class="
              currentPlanIndex >= 0
                ? 'ln-pricing--current-' + currentPlanIndex
                : ''
            "
          >
            <thead>
              <tr>
                <th></th>
                <th>
                  <span class="ln-pricing__plan">Free</span>
                  <span
                    v-if="userPlan.plan.name === 'free'"
                    class="ln-tag ln-tag--blue"
                    >Current</span
                  >
                </th>
                <th>
                  <span class="ln-pricing__plan">Startup</span>
                  <span
                    v-if="userPlan.plan.name === 'startup'"
                    class="ln-tag ln-tag--blue"
                    >Current</span
                  >
                </th>
                <th>
                  <span class="ln-pricing__plan">Pro</span>
                  <span
                    v-if="userPlan.plan.name === 'pro'"
                    class="ln-tag ln-tag--blue"
                    >Current</span
                  >
                </th>
                <th>
                  <span class="ln-pricing__plan">Enterprise</span>
                  <span
                    v-if="userPlan.plan.name === 'enterprise'"
                    class="ln-tag ln-tag--blue"
                    >Current</span
                  >
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Clicks per month limit</td>
                <td><span class="ln-num">100</span></td>
                <td><span class="ln-num">5'000</span></td>
                <td><span class="ln-num">50'000</span></td>
                <td>
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon class="ln-icon-check" v-bind="props"
                        >mdi-all-inclusive</v-icon
                      >
                    </template>
                    <span>No limits</span>
                  </v-tooltip>
                </td>
              </tr>

              <tr>
                <td>Total links</td>
                <td><span class="ln-num">5</span></td>
                <td><span class="ln-num">100</span></td>
                <td>
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon class="ln-icon-check" v-bind="props"
                        >mdi-all-inclusive</v-icon
                      >
                    </template>
                    <span>No limits</span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon class="ln-icon-check" v-bind="props"
                        >mdi-all-inclusive</v-icon
                      >
                    </template>
                    <span>No limits</span>
                  </v-tooltip>
                </td>
              </tr>

              <tr>
                <td>Deep linking support</td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Included"
                    >mdi-check</v-icon
                  >
                </td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Included"
                    >mdi-check</v-icon
                  >
                </td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Included"
                    >mdi-check</v-icon
                  >
                </td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Included"
                    >mdi-check</v-icon
                  >
                </td>
              </tr>

              <tr>
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <td v-bind="props">Number of deep linking apps</td>
                  </template>
                  <span>
                    The number of deep linking apps you can create. You can
                    create multiple links for each deep linking app.
                  </span>
                </v-tooltip>

                <td><span class="ln-num">1</span></td>
                <td><span class="ln-num">5</span></td>
                <td><span class="ln-num">20</span></td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Unlimited"
                    >mdi-all-inclusive</v-icon
                  >
                </td>
              </tr>

              <tr>
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props }">
                    <td v-bind="props">Team members</td>
                  </template>
                  <span> Invite your team to collaborate on your links. </span>
                </v-tooltip>

                <td><span class="ln-num">1</span></td>
                <td><span class="ln-num">3</span></td>
                <td><span class="ln-num">10</span></td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Unlimited"
                    >mdi-all-inclusive</v-icon
                  >
                </td>
              </tr>

              <tr>
                <td>Customize landing page</td>
                <td>
                  <v-icon class="ln-icon-dash" aria-label="Not included"
                    >mdi-minus</v-icon
                  >
                </td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Included"
                    >mdi-check</v-icon
                  >
                </td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Included"
                    >mdi-check</v-icon
                  >
                </td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Included"
                    >mdi-check</v-icon
                  >
                </td>
              </tr>

              <tr>
                <td>Custom domains</td>
                <td>
                  <v-icon class="ln-icon-dash" aria-label="Not included"
                    >mdi-minus</v-icon
                  >
                </td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Included"
                    >mdi-check</v-icon
                  >
                </td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Included"
                    >mdi-check</v-icon
                  >
                </td>
                <td>
                  <v-icon class="ln-icon-check" aria-label="Included"
                    >mdi-check</v-icon
                  >
                </td>
              </tr>

              <tr>
                <td>URL shortener (custom domains)</td>
                <td>
                  <v-icon class="ln-icon-dash" aria-label="Not included"
                    >mdi-minus</v-icon
                  >
                </td>
                <td>
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon class="ln-icon-check" v-bind="props"
                        >mdi-check</v-icon
                      >
                    </template>
                    <span>
                      If you have a custom domain you can customize how it works
                      and
                      <br />add paths to it, e.g. mydomain.com/get ->
                      link-to.app/demo
                    </span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon class="ln-icon-check" v-bind="props"
                        >mdi-check</v-icon
                      >
                    </template>
                    <span>
                      If you have a custom domain you can customize how it works
                      and
                      <br />add paths to it, e.g. mydomain.com/get ->
                      link-to.app/demo
                    </span>
                  </v-tooltip>
                </td>
                <td>
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon class="ln-icon-check" v-bind="props"
                        >mdi-check</v-icon
                      >
                    </template>
                    <span>
                      If you have a custom domain you can customize how it works
                      and
                      <br />add paths to it, e.g. mydomain.com/get ->
                      link-to.app/demo
                    </span>
                  </v-tooltip>
                </td>
              </tr>

              <tr>
                <td>
                  Price VAT inc. ({{
                    billingCycle === "yearly" ? "yearly" : "monthly"
                  }})<template v-if="!priceLoading && pricePreviewCurrencyCode">
                    &middot; {{ pricePreviewCurrencyCode }}</template
                  >
                </td>
                <td><span class="ln-price-cell">Free</span></td>
                <td>
                  <span class="ln-price-cell" v-if="priceLoading"
                    >Loading&hellip;</span
                  >
                  <span class="ln-price-cell" v-else>{{
                    getPlanPrice(1).price
                  }}</span>
                </td>
                <td>
                  <span class="ln-price-cell" v-if="priceLoading"
                    >Loading&hellip;</span
                  >
                  <span class="ln-price-cell" v-else>{{
                    getPlanPrice(2).price
                  }}</span>
                </td>
                <td>
                  <span class="ln-price-cell" v-if="priceLoading"
                    >Loading&hellip;</span
                  >
                  <span class="ln-price-cell" v-else>{{
                    getPlanPrice(3).price
                  }}</span>
                </td>
              </tr>

              <tr v-if="isAdmin" class="ln-pricing__cta-row">
                <td></td>
                <td>
                  <button-of-plan
                    :planName="plans[0].name"
                    :billingCycle="billingCycle"
                  />
                </td>
                <td>
                  <button-of-plan
                    :planName="plans[1].name"
                    :billingCycle="billingCycle"
                  />
                </td>
                <td>
                  <button-of-plan
                    :planName="plans[2].name"
                    :billingCycle="billingCycle"
                  />
                </td>
                <td>
                  <button-of-plan
                    :planName="plans[3].name"
                    :billingCycle="billingCycle"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="displayTxs" class="ln-panel" data-reveal>
        <div class="ln-section__head">
          <v-icon class="ln-section__icon">mdi-receipt-text-outline</v-icon>
          <h2 class="ln-section__title">Receipts</h2>
        </div>
        <div class="ln-receipts-scroll">
          <v-data-table
            height="300"
            :items-per-page="-1"
            :mobile-breakpoint="600"
            :headers="transactionsHeaders"
            :items="transactions"
            :loading="transactionsLoading"
            hide-default-footer
          >
            <template v-slot:item.amount="{ item }">
              <span class="ln-num">{{ item.amount }} {{ item.currency }}</span>
            </template>
            <template v-slot:item.receipt_url="{ item }">
              <a
                class="ln-inline-link"
                :href="item.receipt_url"
                target="_blank"
                rel="noopener"
                >Open</a
              >
            </template>
          </v-data-table>
        </div>
        <div class="ln-panel__footer">
          <download-csv :data="transactions" name="receipts_1link.csv">
            <button type="button" class="ln-btn ln-btn--quiet ln-btn--sm">
              <v-icon size="small" class="ln-btn__icon">mdi-download</v-icon>
              Export CSV
            </button>
          </download-csv>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import ButtonOfPlan from "../components/ButtonOfPlan.vue";
import { mapState, mapGetters } from "vuex";
import {
  listTxs,
  getPaddleSubscription,
  getCustomerPortalUrl,
  getBillingEmail,
  updateBillingEmail,
  handleApiError,
} from "../api";
import JsonCSV from "vue-json-csv";
import { initializePaddle, getPaddleInstance } from "@paddle/paddle-js";

export default {
  components: {
    ButtonOfPlan,
    "download-csv": JsonCSV,
  },
  data() {
    return {
      billingCycle: "monthly",
      paddleUser: undefined,
      transactionsLoading: true,
      openingPaddleUpdateUrl: false,
      // the address on the Paddle customer, where receipts and payment notices
      // go. null until loaded, and for an account with no Paddle customer
      billingEmail: null,
      billingEmailInput: "",
      editingBillingEmail: false,
      savingBillingEmail: false,
      pricePreviews: {},
      pricePreviewCurrencyCode: null,
      priceLoading: true,
      transactionsHeaders: [
        {
          title: "ID",
          key: "order_id", // Classic: integer, Billing: string like 'txn_01hv8z...'
          sortable: false,
        },
        {
          title: "Amount",
          key: "amount",
        },
        {
          title: "Created Date",
          key: "created_at",
        },
        {
          title: "Status",
          key: "status",
        },
        {
          title: "Receipt",
          key: "receipt_url",
        },
      ],
      transactions: [],
    };
  },
  computed: {
    ...mapState(["plans", "userPlan"]),
    ...mapGetters([
      "totalLinksCounter",
      "userPlanObject",
      "monthlyClicksCounter",
      "isAdmin",
    ]),
    linksUsage() {
      return this.usagePercent(
        this.totalLinksCounter,
        this.userPlanObject.max_links,
      );
    },
    clicksUsage() {
      return this.usagePercent(
        this.monthlyClicksCounter,
        this.userPlanObject.monthly_clicks,
      );
    },
    // Check if user has Paddle Billing subscription
    hasBillingSubscription() {
      return !!this.userPlan.user.paddle_billing_subscription_id;
    },
    // Check if user has Paddle Classic subscription
    hasClassicSubscription() {
      return !!this.userPlan.user.paddle_active_subscription_id;
    },
    displayTxs() {
      const u = this.userPlan.user;
      return !!(u.paddle_user_id || u.paddle_billing_customer_id);
    },
    // Determine which payment update flow to use
    useBillingPortal() {
      return this.userPlan.user.paddle_billing_customer_id;
    },
    // Get price for a plan based on billing cycle
    getPlanPrice() {
      return (planIndex) => {
        const plan = this.plans[planIndex];
        if (!plan || planIndex === 0) {
          return { price: "Free" };
        }

        const priceKey = this.billingCycle === "yearly" ? "yearly" : "monthly";
        const priceId = plan[priceKey]?.priceId;

        if (!this.pricePreviews[priceId]) {
          return { price: "Loading..." };
        }

        const line = this.pricePreviews[priceId];
        return { price: line.formattedTotals.total };
      };
    },
    // Highlights the user's current tier in the comparison table; -1 (no
    // match) rather than the array default's coincidental 0 while plans/
    // userPlan are still their empty-object placeholders pre-fetch
    currentPlanIndex() {
      if (!this.userPlan.plan.name) return -1;
      return this.plans.findIndex((p) => p.name === this.userPlan.plan.name);
    },
  },
  mounted() {
    this.init()
      .catch((err) => this.onInitError(err))
      .finally(() => {
        // wait for the async plan/receipts data to settle in the DOM before
        // observing [data-reveal] elements - the Receipts section only exists
        // once displayTxs turns true, so observing synchronously at mount
        // would miss it and leave it stuck at opacity:0 (see the identical
        // fix/comment in Domains.vue's mounted())
        this.$nextTick(() => {
          this.setupReveal();
        });
      });
  },
  beforeUnmount() {
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
  },
  methods: {
    // init() only clears the global loader on success, so a failed request
    // (e.g. the network dropping, Sentry 1LINK-R) left the opaque overlay
    // covering the page until a manual reload
    onInitError(err) {
      handleApiError(err);
      this.$store.commit("setIsLoading", false);
      this.$store.commit("showSnackbarMessage", {
        content:
          "Some plan and pricing details could not be loaded. Please check your connection and reload the page.",
        color: "error",
      });
    },
    setupReveal() {
      const elements = this.$el.querySelectorAll("[data-reveal]");
      const prefersReducedMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // no observer (or the user asked for no motion): show everything at once
      // rather than leaving the page permanently at opacity 0
      if (!("IntersectionObserver" in window) || prefersReducedMotion) {
        elements.forEach((el) => el.classList.add("is-revealed"));
        return;
      }

      this._revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.05 },
      );
      elements.forEach((el) => this._revealObserver.observe(el));
    },
    capitalize(str) {
      // same result as lodash's capitalize: first letter upper, rest lower, "" for null
      const text = String(str ?? "");
      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    },
    // null hides the meter: -1 is the "unlimited" sentinel, and both counters
    // are undefined until getUserPlan resolves
    usagePercent(used, limit) {
      if (typeof limit !== "number" || limit <= 0) return null;
      if (typeof used !== "number") return null;
      return Math.min(100, Math.round((used / limit) * 100));
    },
    formatCount(value) {
      return typeof value === "number" ? value.toLocaleString() : value;
    },
    getDayDate(fullIsoDate) {
      return fullIsoDate.split("T")[0];
    },
    async fetchPricePreviews() {
      try {
        this.priceLoading = true;

        // Collect all price IDs from plans
        const priceIds = [];
        this.plans.forEach((plan) => {
          if (plan.monthly?.priceId) priceIds.push(plan.monthly.priceId);
          if (plan.yearly?.priceId) priceIds.push(plan.yearly.priceId);
        });

        if (priceIds.length === 0) {
          this.pricePreviewCurrencyCode = null;
          this.priceLoading = false;
          return;
        }

        // Fetch price previews using Paddle.PricePreview
        const paddleInstance = getPaddleInstance("v1");
        const pricePreviewData = await paddleInstance.PricePreview({
          items: priceIds.map((priceId) => ({ priceId, quantity: 1 })),
        });
        // const pricePreviewData = await window.Paddle.PricePreview({
        //   items: priceIds.map(priceId => ({ priceId, quantity: 1 }))
        // });
        console.log("pricePreviewData", pricePreviewData);

        // Store price previews by price ID
        const priceMap = {};
        pricePreviewData.data.details.lineItems.forEach((item) => {
          priceMap[item.price.id] = item;
        });

        this.pricePreviews = priceMap;
        this.pricePreviewCurrencyCode =
          pricePreviewData.data.currencyCode ||
          pricePreviewData.data.details.lineItems[0]?.price?.unitPrice
            ?.currencyCode ||
          null;
        this.priceLoading = false;
      } catch (error) {
        console.error("Failed to fetch price previews:", error);
        this.pricePreviewCurrencyCode = null;
        this.priceLoading = false;
      }
    },
    async init() {
      await this.$store.dispatch("getPlans");
      await this.$store.dispatch("getUserPlan");

      const paddleSetup = {
        token: "live_0dd5f5c2ad68e09b0fe08d2af02",
      };
      if (this.userPlan.user.paddle_billing_customer_id) {
        paddleSetup.pwCustomer = {
          id: this.userPlan.user.paddle_billing_customer_id,
        };
      }
      await initializePaddle(paddleSetup);
      // window.Paddle.Setup(paddleSetup);

      // Fetch price previews after plans are loaded
      await this.fetchPricePreviews();

      if (this.displayTxs) {
        if (this.hasClassicSubscription) {
          this.fetchPaddleUser();
        }
        this.getTransactions();
      }

      if (this.userPlan.user.paddle_billing_customer_id) {
        this.fetchBillingEmail();
      }

      this.$store.commit("setIsLoading", false);
    },
    // not awaited by init(): the page neither waits for this call nor fails
    // with it, the line simply stays hidden
    async fetchBillingEmail() {
      try {
        const res = await getBillingEmail();
        this.billingEmail = res.email;
      } catch (err) {
        handleApiError(err);
      }
    },
    editBillingEmail() {
      this.billingEmailInput = this.billingEmail;
      this.editingBillingEmail = true;
    },
    async saveBillingEmail() {
      const email = this.billingEmailInput.trim();
      if (email === this.billingEmail) {
        this.editingBillingEmail = false;
        return;
      }
      // same check as the Account page, the api validates it again
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.$store.commit("showSnackbarMessage", {
          content: "Please enter a valid email address.",
          color: "error",
        });
        return;
      }

      this.savingBillingEmail = true;
      try {
        const res = await updateBillingEmail(email);
        this.billingEmail = res.email;
        this.editingBillingEmail = false;
        this.$store.commit("showSnackbarMessage", {
          content: "Billing email updated",
          color: "success",
        });
      } catch (err) {
        this.$store.commit("showSnackbarMessage", {
          content: handleApiError(err, "Failed to update the billing email"),
          color: "error",
        });
      } finally {
        this.savingBillingEmail = false;
      }
    },
    async getTransactions() {
      this.transactionsLoading = true;
      const list = await listTxs();
      this.transactions = list;
      this.transactionsLoading = false;
    },
    async fetchPaddleUser() {
      const res = await getPaddleSubscription();

      this.paddleUser = res;
    },
    async openPaddleUpdateUrl() {
      try {
        this.openingPaddleUpdateUrl = true;

        if (this.useBillingPortal) {
          // Use Paddle Billing customer portal
          const result = await getCustomerPortalUrl();

          if (result && result.url) {
            window.open(result.url, "_blank");
          } else {
            window.alert(
              "Unable to open customer portal. Please contact support.",
            );
          }
        } else {
          // Use Paddle Classic update URL (fallback)
          const res = await getPaddleSubscription();
          const paddleUpdateUrl = res ? res.update_url : undefined;

          if (typeof paddleUpdateUrl !== "string") {
            this.openingPaddleUpdateUrl = false;
            window.alert(
              "Unable to retrieve your subscription, check your latest emails to update the payment method",
            );
            return;
          }

          // Open Paddle Classic checkout with override
          window.Paddle.Checkout.open({
            override: paddleUpdateUrl,
          });
        }
      } catch (err) {
        console.error(err);
        window.alert(
          "Error opening payment update portal. Please try again or contact support.",
        );
      } finally {
        this.openingPaddleUpdateUrl = false;
      }
    },
  },
};
</script>

<style scoped>
/* ---------- canvas ---------------------------------------------------- */

.ln-page {
  min-height: 100%;
  background: var(--ln-canvas);
}

.ln-wrap {
  padding: 20px 16px 40px;
}

/* ---------- scroll entry ---------------------------------------------- */

[data-reveal] {
  opacity: 0;
  transform: translateY(12px);
  transition:
    opacity 600ms var(--ln-ease),
    transform 600ms var(--ln-ease);
}

[data-reveal].is-revealed {
  opacity: 1;
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  [data-reveal],
  [data-reveal].is-revealed {
    opacity: 1;
    transform: none;
    transition: none;
  }
}

/* ---------- page header ------------------------------------------------ */

.ln-head {
  margin-bottom: 24px;
}

.ln-eyebrow {
  margin: 0 0 10px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-display {
  margin: 0;
  font-family: sans-serif;
  font-size: 40px;
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: var(--ln-ink);
}

.ln-lede {
  margin: 10px 0 0;
  max-width: 52ch;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ln-muted);
}

/* ---------- buttons ------------------------------------------------------ */

.ln-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 16px;
  height: 38px;
  border: 1px solid transparent;
  border-radius: var(--ln-radius-sm);
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease),
    transform 120ms var(--ln-ease);
}

.ln-btn:active {
  transform: scale(0.98);
}

.ln-btn:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-btn__icon.v-icon {
  font-size: 15px;
  color: inherit;
}

.ln-btn--quiet {
  border-color: var(--ln-line);
  background: var(--ln-surface);
  color: var(--ln-ink-2);
}

.ln-btn--quiet:hover {
  background: var(--ln-surface-2);
  border-color: #dcdcdc;
}

.ln-btn--sm {
  height: 28px;
  padding: 0 12px;
  font-size: 12px;
}

.ln-btn:disabled {
  opacity: 0.45;
  cursor: default;
  transform: none;
}

.ln-inline-link {
  font-size: 12px;
  font-weight: 500;
  color: var(--ln-ink-2);
  text-decoration: none;
  border-bottom: 1px solid var(--ln-line);
  transition:
    border-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-page a.ln-inline-link {
  color: var(--ln-ink-2);
}

.ln-inline-link:hover {
  color: var(--ln-ink);
  border-bottom-color: var(--ln-ink);
}

.ln-spin {
  animation: ln-spin 900ms linear infinite;
}

@keyframes ln-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ---------- current plan card ------------------------------------------- */

.ln-plan-card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 22px;
  margin-bottom: 16px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
}

.ln-plan-card__label {
  margin: 0 0 8px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-plan-card__value {
  margin: 0;
  font-family: sans-serif;
  font-size: 30px;
  font-weight: 400;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: var(--ln-ink);
}

.ln-plan-card__meta {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--ln-muted);
}

.ln-plan-card__meta--warn {
  color: var(--ln-red-fg);
  font-weight: 500;
}

.ln-plan-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* the "Edit" next to the billing email is a button drawn as an inline link */
button.ln-inline-link {
  margin-left: 6px;
  padding: 0;
  border-width: 0 0 1px;
  background: none;
  font-family: inherit;
  cursor: pointer;
}

/* the info column takes the room left by the actions and may shrink, so the
   billing email form below has a width to fill and a long address wraps
   instead of pushing the actions out of the card */
.ln-plan-card__info {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

.ln-billing-email {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  max-width: 440px;
  margin-top: 12px;
}

.ln-billing-email__input {
  flex: 1 1 220px;
}

/* one unit, so on a narrow card both buttons move under the field together */
.ln-billing-email__actions {
  display: flex;
  gap: 8px;
}

/* same field outline as the app's dialogs: --ln-line alone is too faint */
.ln-billing-email :deep(.v-field__outline) {
  color: color-mix(in srgb, var(--ln-ink) 35%, var(--ln-line));
  --v-field-border-opacity: 1;
}

.ln-billing-email :deep(.v-field--focused .v-field__outline) {
  color: var(--ln-accent);
}

/* ---------- usage tiles -------------------------------------------------- */

.ln-bento {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.ln-tile {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 20px;
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
}

.ln-tile__label {
  margin: 0 0 14px;
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ln-muted);
  word-break: normal;
}

.ln-tile__value {
  margin: 0;
  font-family: sans-serif;
  font-size: 30px;
  font-weight: 300;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: var(--ln-ink);
  font-variant-numeric: tabular-nums;
}

.ln-tile__meta {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--ln-muted);
}

.ln-meter {
  height: 2px;
  margin-top: 14px;
  background: var(--ln-line);
  overflow: hidden;
}

.ln-meter__fill {
  display: block;
  min-width: 3px;
  height: 100%;
  background: var(--ln-ink);
  transition: width 600ms var(--ln-ease);
}

/* ---------- panel / section head ------------------------------------------ */

.ln-panel {
  border: 1px solid var(--ln-line);
  border-radius: var(--ln-radius);
  background: var(--ln-surface);
  overflow: hidden;
  margin-bottom: 20px;
}

.ln-panel:last-child {
  margin-bottom: 0;
}

.ln-section__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--ln-line);
}

.ln-section__icon.v-icon {
  font-size: 19px;
  color: var(--ln-muted);
}

.ln-section__title {
  margin: 0;
  flex: 1 1 auto;
  font-family: sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ln-ink);
}

.ln-section__head-link {
  flex: 0 0 auto;
}

.ln-panel__footer {
  display: flex;
  justify-content: flex-end;
  padding: 14px 20px;
  border-top: 1px solid var(--ln-line);
}

/* ---------- billing cycle toggle ------------------------------------------- */

.ln-toggle-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 20px 16px;
}

.ln-toggle {
  display: inline-flex;
  padding: 3px;
  border: 1px solid var(--ln-line);
  border-radius: 9999px;
  background: var(--ln-surface-2);
}

.ln-toggle__opt {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: var(--ln-muted);
  cursor: pointer;
  transition:
    background-color 200ms var(--ln-ease),
    color 200ms var(--ln-ease);
}

.ln-toggle__opt.is-active {
  background: var(--ln-surface);
  color: var(--ln-ink);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.07);
}

.ln-toggle__opt:focus-visible {
  outline: 2px solid var(--ln-ink);
  outline-offset: 2px;
}

.ln-toggle__badge {
  padding: 1px 6px;
  border-radius: 9999px;
  background: var(--ln-green-bg);
  color: var(--ln-green-fg);
  font-size: 10px;
  font-weight: 600;
}

.ln-toggle-row__note {
  margin: 0;
  font-size: 12px;
  color: var(--ln-muted);
}

.ln-toggle-row__note strong {
  color: var(--ln-ink-2);
  font-weight: 500;
}

/* ---------- tags --------------------------------------------------------- */

.ln-tag {
  display: inline-block;
  margin-top: 6px;
  padding: 3px 9px;
  border-radius: 9999px;
  font-family: sans-serif;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: default;
}

.ln-tag--blue {
  background: var(--ln-blue-bg);
  color: var(--ln-blue-fg);
}

/* ---------- pricing table -------------------------------------------------- */

.ln-pricing-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-top: 1px solid var(--ln-line);
}

.ln-pricing {
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;
}

.ln-pricing th,
.ln-pricing td {
  padding: 14px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--ln-ink-2);
  border-bottom: 1px solid var(--ln-line);
}

.ln-pricing thead th {
  padding-top: 18px;
  padding-bottom: 16px;
  background: var(--ln-surface-2);
  vertical-align: top;
}

.ln-pricing__plan {
  display: block;
  font-family: sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--ln-ink);
}

.ln-pricing tbody td:first-child {
  text-align: left;
  font-size: 13px;
  font-weight: 500;
  color: var(--ln-ink-2);
  white-space: nowrap;
}

.ln-pricing tbody tr:last-child td {
  border-bottom: 0;
}

.ln-pricing tbody tr:hover td {
  background: var(--ln-surface-2);
}

.ln-pricing__cta-row td {
  padding-top: 16px;
  padding-bottom: 20px;
}

.ln-pricing__cta-row:hover td {
  background: transparent;
}

.ln-num {
  font-family: var(--ln-mono);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--ln-ink);
}

.ln-price-cell {
  font-family: sans-serif;
  font-size: 15px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--ln-ink);
}

.ln-icon-check.v-icon {
  font-size: 18px;
  color: var(--ln-ink);
}

.ln-icon-dash.v-icon {
  font-size: 18px;
  color: var(--ln-muted);
}

/* highlights the signed-in user's tier; index maps to nth-child(idx + 2)
   since column 1 is the row label, not a plan */
.ln-pricing--current-0 thead th:nth-child(2),
.ln-pricing--current-0 tbody tr:not(:hover) td:nth-child(2) {
  background: var(--ln-blue-bg);
}
.ln-pricing--current-1 thead th:nth-child(3),
.ln-pricing--current-1 tbody tr:not(:hover) td:nth-child(3) {
  background: var(--ln-blue-bg);
}
.ln-pricing--current-2 thead th:nth-child(4),
.ln-pricing--current-2 tbody tr:not(:hover) td:nth-child(4) {
  background: var(--ln-blue-bg);
}
.ln-pricing--current-3 thead th:nth-child(5),
.ln-pricing--current-3 tbody tr:not(:hover) td:nth-child(5) {
  background: var(--ln-blue-bg);
}

/* ---------- receipts table ------------------------------------------------- */

.ln-receipts-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* every vuetify rule reached from here lives in an @layer, and unlayered
   styles beat any layer regardless of specificity - hence no !important
   below, unlike the Vuetify 2 version of this block */
.ln-panel :deep(.v-data-table) {
  --v-table-header-height: 42px;
  --v-table-row-height: 52px;
  background: transparent;
  color: var(--ln-ink-2);
}

.ln-panel :deep(th.v-data-table__th) {
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--ln-muted);
  background: var(--ln-surface-2);
  border-bottom: 1px solid var(--ln-line);
}

.ln-panel :deep(td.v-data-table__td) {
  font-size: 14px;
  color: var(--ln-ink-2);
  border-bottom: 1px solid var(--ln-line);
}

.ln-panel :deep(.v-data-table__tbody > tr:last-child > td.v-data-table__td) {
  border-bottom: 0;
}

.ln-panel :deep(.v-data-table__tbody > .v-data-table__tr:hover > td) {
  background: var(--ln-surface-2);
}

.ln-panel :deep(.v-data-table-rows-no-data > td),
.ln-panel :deep(.v-data-table-rows-loading > td) {
  height: auto;
  background: transparent;
  border-bottom: 0;
  color: inherit;
}

/* below mobile-breakpoint (600) each record is one
   <tr class="v-data-table__tr--mobile"> of .v-data-table__td-title /
   .v-data-table__td-value pairs */
.ln-panel :deep(.v-data-table__tr--mobile) {
  --v-table-row-height: 42px;
}

/* vuetify clears this itself, but from inside a layer the unlayered td rule
   above outranks - restate it so the hairline lands once per record */
.ln-panel :deep(.v-data-table__tr--mobile > td:not(:last-child)) {
  border-bottom: 0;
}

.ln-panel :deep(.v-data-table__td-title) {
  font-family: var(--ln-mono);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ln-muted);
}

.ln-panel :deep(.v-data-table__td-value) {
  font-size: 14px;
  color: var(--ln-ink-2);
}

.ln-panel :deep(.v-data-table-progress .v-progress-linear__indeterminate.long),
.ln-panel :deep(.v-data-table-progress .v-progress-linear__indeterminate.short),
.ln-panel :deep(.v-data-table-progress .v-progress-linear__determinate) {
  background-color: var(--ln-ink);
  border-color: var(--ln-ink);
}

.ln-panel :deep(.v-data-table-progress .v-progress-linear__background) {
  background-color: var(--ln-line);
  border-color: var(--ln-line);
}

/* ---------- breakpoints ------------------------------------------------ */

@media (min-width: 600px) {
  .ln-wrap {
    padding: 28px 20px 56px;
  }

  .ln-display {
    font-size: 52px;
  }

  .ln-plan-card {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 26px 28px;
  }

  .ln-plan-card__value {
    font-size: 36px;
  }

  .ln-plan-card__actions {
    flex: 0 0 auto;
  }

  .ln-tile {
    padding: 26px;
  }

  .ln-tile__value {
    font-size: 36px;
  }
}

@media (min-width: 960px) {
  .ln-wrap {
    padding: 36px 24px 64px;
  }
}
</style>
